import React, { ReactNode } from 'react'

import { HeaderButton, Dots, ButtonWrapper } from './ProjectHeader.styles'
import { Dropdown } from '@sketch/components'

import {
  CopyProjectAction,
  DeleteProjectAction,
  DeleteProjectPermanentlyAction,
  LeaveProjectAction,
  RenameProjectAction,
  RestoreProjectAction,
} from '../ProjectDropdownActions'

import ProjectMembershipsTooltip from '../ProjectMembershipsTooltip'

import SharingButton from './SharingButton'

import { useGetProjectMembershipsQuery } from '@sketch/gql-types'
import { ProjectHeaderActionsProps } from './types'
import { useResponsiveDropdown } from 'components/ResponsiveDropdown'
import { useFlag } from 'containers/flags'

const ActionDropdownWrapper = ({ children }: { children: ReactNode }) => (
  <div onClick={event => event.stopPropagation()}>
    <Dropdown.Header>Project</Dropdown.Header>
    {children}
  </div>
)

export const ProjectHeaderActions: React.FC<ProjectHeaderActionsProps> = ({
  project,
  workspaceAccessLevel,
  workspace,
  userCanRenameAndDeleteProject,
  onRenameProject,
}) => {
  const { identifier: workspaceId } = workspace
  const { error, loading, data } = useGetProjectMembershipsQuery({
    variables: { projectIdentifier: project.identifier },
  })

  let dropdownAction = null

  const hasGuestsInProjects = useFlag('guests-in-project')

  // The `Leave...` button is being added as part of the guests-in-projects feature,
  // only users with the feature flag enabled AND who can leave the project
  // should see the button.
  const showLeaveAction = hasGuestsInProjects && project.userCanLeave

  // Only show the divider if there will be buttons beneath it
  const showDivider = userCanRenameAndDeleteProject || showLeaveAction

  if (project.deletedAt) {
    dropdownAction = (
      <>
        <CopyProjectAction project={project} workspaceId={workspaceId} />
        <RestoreProjectAction project={project} workspaceId={workspaceId} />
        <Dropdown.Divider />
        {showLeaveAction && (
          <LeaveProjectAction
            projectIdentifier={project.identifier}
            projectName={project.name}
          />
        )}
        <DeleteProjectPermanentlyAction
          project={project}
          workspaceId={workspaceId}
        />
      </>
    )
  } else {
    dropdownAction = (
      <>
        <CopyProjectAction project={project} workspaceId={workspaceId} />
        {userCanRenameAndDeleteProject && (
          <RenameProjectAction onRename={() => onRenameProject(true)} />
        )}
        {showDivider && <Dropdown.Divider />}
        {showLeaveAction && (
          <LeaveProjectAction
            projectIdentifier={project.identifier}
            projectName={project.name}
          />
        )}
        {userCanRenameAndDeleteProject && (
          <DeleteProjectAction project={project} workspaceId={workspaceId} />
        )}
      </>
    )
  }

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: ActionDropdownWrapper,
    dropdownProps: { children: dropdownAction },
    usePortal: true,
    placement: 'bottom-start',
  })

  if (project.type === 'PERSONAL_DRAFTS') {
    return null
  }

  return (
    <ButtonWrapper>
      <ProjectMembershipsTooltip
        workspace={workspace}
        memberships={data?.project.memberships}
        shouldShowWorkspace={workspaceAccessLevel !== 'NONE'}
        workspaceAccessLevel={workspaceAccessLevel}
        disabled={loading || !!error}
      >
        <SharingButton
          project={project}
          workspace={workspace}
          workspaceAccessLevel={workspaceAccessLevel}
          userCanRenameAndDeleteProject={userCanRenameAndDeleteProject}
          memberships={data?.project.memberships}
        />
      </ProjectMembershipsTooltip>

      <>
        <HeaderButton {...buttonProps}>
          <span className="sr-only">Other Actions</span>
          <Dots />
        </HeaderButton>
        {content}
      </>
    </ButtonWrapper>
  )
}
